/**
 * GlobalView — Globe Viewer (§11.4, Phase 2)
 *
 * React wrapper around CesiumJS Viewer.
 * Supports switching between Google 3D Tiles and Cesium Standard.
 */

import { useRef, useEffect, useState, useCallback } from 'react';
import * as Cesium from 'cesium';

import { useGlobeStore, type TileMode } from '../../stores/globeStore';
import { loadGoogle3DTiles, loadOsmBuildings, loadFallbackTerrain } from '../../services/tiles3d';
import { setBaseLayerStyle, configureGlobeAppearance } from '../../services/baseLayer';
import { applyVisualMode, clearVisualModes } from '../../services/visualModeService';
import { watchCameraPosition, watchCursorPosition, watchViewport } from '../../services/cameraService';
import { installSelectionHandler } from '../../services/selectionHandler';
import { installKeyboardShortcuts } from '../../services/keyboardShortcuts';
import { initAirLayer, destroyAirLayer, highlightAirEntity } from '../../services/layers/AirLayerRenderer';
import { initRailLayer, destroyRailLayer, highlightRailEntity } from '../../services/layers/RailLayerRenderer';
import { initRailTrackRenderer, destroyRailTrackRenderer } from '../../services/layers/railTrackRenderer';
import { initSpaceLayer, destroySpaceLayer } from '../../services/layers/SpaceLayerRenderer';
import { initEWLayer, destroyEWLayer } from '../../services/layers/EWLayerRenderer';
import { initZonesLayer, destroyZonesLayer } from '../../services/layers/ZonesLayerRenderer';
import { initSeaLayer, destroySeaLayer, highlightSeaEntity } from '../../services/layers/SeaLayerRenderer';
import { initQuakeLayer, destroyQuakeLayer } from '../../services/layers/QuakeLayerRenderer';
import { initFireLayer, destroyFireLayer } from '../../services/layers/FireLayerRenderer';
import { initEonetLayer, destroyEonetLayer } from '../../services/layers/EonetLayerRenderer';
import { connect as wsConnect, disconnect as wsDisconnect, sendViewport } from '../../services/wsClient';
import { startTracking, stopTracking } from '../../services/aircraftTracker';
import { startSatelliteTracking, stopSatelliteTracking } from '../../services/satelliteTracker';
import { useEntityStore } from '../../stores/entityStore';
import HudOverlay from './HudOverlay';
import SelectionMarker from './SelectionMarker';
import AircraftPopup from '../ui/AircraftPopup';
import TrainPopup from '../ui/TrainPopup';
import SatellitePopup from '../ui/SatellitePopup';
import ZonePopup from '../ui/ZonePopup';
import VesselPopup from '../ui/VesselPopup';
import EarthquakePopup from '../ui/EarthquakePopup';
import WildfirePopup from '../ui/WildfirePopup';
import NaturalEventPopup from '../ui/NaturalEventPopup';
import styles from './GlobeViewer.module.css';

// Cesium Ion access token (from env)
const CESIUM_TOKEN = import.meta.env.VITE_CESIUM_ION_TOKEN as string | undefined;
const GOOGLE_MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;

export default function GlobeViewer() {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<Cesium.Viewer | null>(null);
  const [loading, setLoading] = useState(true);

  // Refs to track loaded primitives for switching
  const google3dRef = useRef<Cesium.Cesium3DTileset | null>(null);
  const osmBuildingsRef = useRef<Cesium.Cesium3DTileset | null>(null);

  const setViewer = useGlobeStore((s) => s.setViewer);
  const updateCameraPosition = useGlobeStore((s) => s.updateCameraPosition);
  const setCursorPosition = useGlobeStore((s) => s.setCursorPosition);
  const setSelectedEntity = useGlobeStore((s) => s.setSelectedEntity);
  const setReady = useGlobeStore((s) => s.setReady);
  const tileMode = useGlobeStore((s) => s.tileMode);
  const visualMode = useGlobeStore((s) => s.visualMode);
  const filterSettings = useGlobeStore((s) => s.filterSettings);

  // ─── Tile Mode Switching ──────────────────────────────────

  const applyTileMode = useCallback((mode: TileMode, viewer: Cesium.Viewer) => {
    if (viewer.isDestroyed()) return;

    if (mode === 'google3d') {
      // Show Google 3D Tiles, hide OSM Buildings
      if (google3dRef.current) google3dRef.current.show = true;
      if (osmBuildingsRef.current) osmBuildingsRef.current.show = false;
      viewer.imageryLayers.removeAll();
      viewer.scene.globe.show = false;
    } else {
      // Show OSM Buildings + base imagery, hide Google 3D Tiles
      if (google3dRef.current) google3dRef.current.show = false;
      if (osmBuildingsRef.current) osmBuildingsRef.current.show = true;
      viewer.scene.globe.show = true;
      // Re-add base imagery if not present
      if (viewer.imageryLayers.length === 0) {
        setBaseLayerStyle(viewer, 'standard');
      }
    }
  }, []);

  // React to tileMode changes
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || viewer.isDestroyed()) return;
    applyTileMode(tileMode, viewer);
  }, [tileMode, applyTileMode]);

  // ─── Visual Mode Switching ─────────────────────────────────

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || viewer.isDestroyed()) return;
    applyVisualMode(viewer, visualMode, filterSettings);
  }, [visualMode, filterSettings]);

  // ─── Viewer Initialization ────────────────────────────────

  useEffect(() => {
    if (!containerRef.current) return;

    // Configure Cesium Ion token
    if (CESIUM_TOKEN) {
      Cesium.Ion.defaultAccessToken = CESIUM_TOKEN;
    }

    // ─── Create Viewer ────────────────────────────────────────

    const viewer = new Cesium.Viewer(containerRef.current, {
      // Disable all default UI widgets — we build our own
      animation: false,
      timeline: false,
      fullscreenButton: false,
      geocoder: false,
      homeButton: false,
      infoBox: false,
      sceneModePicker: false,
      selectionIndicator: false,
      navigationHelpButton: false,
      baseLayerPicker: false,
      creditContainer: document.createElement('div'),

      // Performance
      requestRenderMode: true,
      maximumRenderTimeChange: Infinity,
      targetFrameRate: 60,
      msaaSamples: 2,

      // Scene
      shadows: false,
      shouldAnimate: true,
    });

    viewerRef.current = viewer;
    setViewer(viewer);

    // Force continuous rendering for smooth interaction
    viewer.scene.requestRenderMode = false;

    // ─── Configure Appearance ─────────────────────────────────

    configureGlobeAppearance(viewer);

    // ─── Load BOTH tile sets (preload for fast switching) ─────

    const initAsync = async () => {
      if (viewer.isDestroyed()) return;

      try {
        // Load both in parallel for fast switching
        const [googleTileset, osmTileset] = await Promise.all([
          GOOGLE_MAPS_KEY
            ? loadGoogle3DTiles(viewer, GOOGLE_MAPS_KEY)
            : Promise.resolve(null),
          loadOsmBuildings(viewer),
        ]);

        if (viewer.isDestroyed()) return;

        // Also load terrain (used with Cesium mode)
        await loadFallbackTerrain(viewer);

        if (viewer.isDestroyed()) return;

        // Store refs
        google3dRef.current = googleTileset;
        osmBuildingsRef.current = osmTileset;

        // If no Google tiles available, force cesium mode
        const currentMode = useGlobeStore.getState().tileMode;
        if (!googleTileset && currentMode === 'google3d') {
          useGlobeStore.getState().setTileMode('cesium');
        }

        // Apply current tile mode
        applyTileMode(useGlobeStore.getState().tileMode, viewer);
      } catch (err) {
        console.error('[GlobeViewer] Initialization error:', err);
      } finally {
        if (!viewer.isDestroyed()) {
          setLoading(false);
          setReady(true);
        }
      }
    };

    initAsync();

    // ─── Camera Tracking ──────────────────────────────────────

    const removeCamera = watchCameraPosition(viewer, updateCameraPosition);
    const removeCursor = watchCursorPosition(viewer, setCursorPosition);
    const removeViewport = watchViewport(viewer, sendViewport);

    // ─── Selection Handler ────────────────────────────────────

    const removeSelection = installSelectionHandler(viewer, {
      onSelect: (id: string | null) => {
        // Deselect previous
        highlightAirEntity(null);
        highlightRailEntity(null);
        highlightSeaEntity(null);
        stopTracking();
        stopSatelliteTracking();

        if (id) {
          setSelectedEntity(id);

          const entity = useEntityStore.getState().entities.get(id);
          if (entity?.type === 'aircraft') {
            highlightAirEntity(id);
            startTracking(id);
          } else if (entity?.type === 'train') {
            highlightRailEntity(id);
          } else if (entity?.type === 'satellite') {
            startSatelliteTracking(id);
          } else if (entity?.type === 'ship') {
            highlightSeaEntity(id);
          }
        } else {
          setSelectedEntity(null);
        }
      },
      onHover: () => { /* cursor updated by selectionHandler */ },
    });

    // ─── Keyboard Shortcuts ───────────────────────────────────

    const removeKeyboard = installKeyboardShortcuts(viewer);

    // ─── Air Layer ───────────────────────────────────────────

    initAirLayer(viewer);

    // ─── Rail Layer ──────────────────────────────────────────

    initRailLayer(viewer);
    initRailTrackRenderer(viewer);

    // ─── Space Layer ─────────────────────────────────────────

    initSpaceLayer(viewer);

    // ─── EW / Signal Layer ───────────────────────────────────

    initEWLayer(viewer);

    // ─── Zones Layer ─────────────────────────────────────────

    initZonesLayer(viewer);

    // ─── Sea Layer ───────────────────────────────────────────

    initSeaLayer(viewer);

    // ─── Quake Layer ─────────────────────────────────────────

    initQuakeLayer(viewer);

    // ─── Fire Layer ──────────────────────────────────────────

    initFireLayer(viewer);

    // ─── EONET Layer ─────────────────────────────────────────

    initEonetLayer(viewer);

    // ─── WebSocket Connection ────────────────────────────────
    // Connect if any live layer is enabled
    const globeState = useGlobeStore.getState();
    if (globeState.airLayerVisible || globeState.railLayerVisible || globeState.spaceLayerVisible || globeState.seaLayerVisible) {
      wsConnect();
    }

    // ─── Initial Camera — always start at Home (Europe) ─────

    viewer.camera.setView({
      destination: Cesium.Cartesian3.fromDegrees(8.54379, 47.36339, 644),
      orientation: {
        heading: Cesium.Math.toRadians(348.3),
        pitch: Cesium.Math.toRadians(-20.5),
        roll: 0,
      },
    });

    // ─── Cleanup ──────────────────────────────────────────────

    return () => {
      removeCamera();
      removeCursor();
      removeViewport();
      removeSelection();
      removeKeyboard();
      stopTracking();
      destroyAirLayer();
      destroyRailLayer();
      destroyRailTrackRenderer();
      destroySpaceLayer();
      destroyEWLayer();
      destroyZonesLayer();
      destroySeaLayer();
      destroyQuakeLayer();
      destroyFireLayer();
      destroyEonetLayer();
      wsDisconnect();
      // Clear visual mode post-processing stages
      if (viewerRef.current && !viewerRef.current.isDestroyed()) {
        clearVisualModes(viewerRef.current);
      }
      setViewer(null);
      setReady(false);
      google3dRef.current = null;
      osmBuildingsRef.current = null;

      if (!viewer.isDestroyed()) {
        viewer.destroy();
      }
      viewerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.container} id="globe-container">
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />

      {loading && (
        <div className={styles.loading}>
          <div className={styles.loadingSpinner} />
          <span className={styles.loadingText}>Initializing Globe…</span>
        </div>
      )}

      <HudOverlay />
      <SelectionMarker />
      <AircraftPopup />
      <TrainPopup />
      <SatellitePopup />
      <ZonePopup />
      <VesselPopup />
      <EarthquakePopup />
      <WildfirePopup />
      <NaturalEventPopup />
    </div>
  );
}
